import { all, takeLatest, put, call, select } from 'redux-saga/effects';
import * as actions from './actions';
import * as constants from './constants';
import { searchParametersSelector } from '../Serp/selectors';
import { subscriptionIdSelector } from './selectors';
import { trackEvent } from '../tracking/actions';
import { resetPermissionsBanner } from '../Index/actions';
import { notificationStatusesEvent } from '../tracking/events';
import clientConfig from '../../parameters/client/config';
import NotificationApi from '../sdk/notifications/webPush/ApiNotifications';
import { getSubscriptionIdFromErrorMessage } from './utils';
import { configSelector } from '~/common/selectors/config';
import { installationIdSelector } from '~/common/selectors/trovit';

const notificationsApi = new NotificationApi(clientConfig.notificationApi);

export function* requestSearchesSaga(data) {
  const { subscriptionId } = data;
  try {
    const result = yield call([notificationsApi, notificationsApi.getSearches], subscriptionId);
    if (result) {
      yield put(actions.fetchSearchesSuccess({ user: result.data, search: result.included }));
    }
  } catch (error) {
    // reset push permissions subscription and ask for them again(that happens when we redirect a country from php to pwa)
    if (error.code === 404) {
      yield put(resetPermissionsBanner());
      yield put(actions.resetUserSubscription());
    }

    yield put(actions.fetchSearchesFailed(error));
  }
}

export function* saveSearchSaga() {
  let subscriptionId;
  try {
    const config = yield select(configSelector);
    const searchParameters = yield select(searchParametersSelector);
    subscriptionId = yield select(subscriptionIdSelector);

    const result = yield call([notificationsApi, notificationsApi.saveSearch], {
      searchParameters,
      subscriptionId,
      countryCode: config.countryCode,
      verticalId: config.verticalId,
    });
    if (result) {
      yield put(
        trackEvent(
          notificationStatusesEvent(
            result.data.attributes.infrastructure_id,
            constants.TRACKING_STATUSES_NEW_SEARCH
          )
        )
      );
      yield put(
        trackEvent(
          notificationStatusesEvent(
            result.data.attributes.infrastructure_id,
            constants.TRACKING_STATUSES_SUBSCRIBED
          )
        )
      );
      yield put(actions.saveSearchSuccess(result.data));
    }
  } catch (error) {
    if (error.code === 409) {
      // update searches of the current user, for any reason some users have outdated the searches that have in the browser.
      yield put(actions.fetchSearches(subscriptionId));
    }
    yield put(actions.saveSearchFailed(error));
  }
}

export function* updateSearchSaga(data) {
  const { searchId, isActive } = data;
  try {
    const searchParameters = yield select(searchParametersSelector);
    const subscriptionId = yield select(subscriptionIdSelector);

    const result = yield call([notificationsApi, notificationsApi.editSearch], {
      searchParameters,
      subscriptionId,
      searchId,
      isActive,
    });
    if (result && result.data) {
      if (typeof isActive !== 'undefined') {
        const status = isActive
          ? constants.TRACKING_STATUSES_SUBSCRIBED
          : constants.TRACKING_STATUSES_UNSUBSCRIBED;
        yield put(
          trackEvent(notificationStatusesEvent(result.data.attributes.infrastructure_id, status))
        );
      }
      yield put(actions.updateSearchSuccess(result.data));
    }
  } catch (error) {
    yield put(actions.updateSearchFailed(error));
  }
}

export function* suscribeUserSaga(data) {
  const { subscription } = data;
  try {
    const config = yield select(configSelector);
    const installationId = yield select(installationIdSelector);
    const searchParameters = yield select(searchParametersSelector);

    const result = yield call([notificationsApi, notificationsApi.suscribeUser], {
      installationId,
      searchParameters,
      subscription,
      countryCode: config.countryCode,
      verticalId: config.verticalId,
    });

    if (result) {
      yield put(actions.suscribeUserSuccess({ user: result.data, search: result.included }));
      yield put(
        trackEvent(
          notificationStatusesEvent(
            result.included[0].attributes.infrastructure_id,
            constants.TRACKING_STATUSES_NEW_SEARCH,
            result.data.attributes.infrastructure_id
          )
        )
      );
      yield put(
        trackEvent(
          notificationStatusesEvent(
            result.included[0].attributes.infrastructure_id,
            constants.TRACKING_STATUSES_SUBSCRIBED,
            result.data.attributes.infrastructure_id
          )
        )
      );
    }
  } catch (error) {
    if (error.code === 409) {
      // restore push subscription (that happens when we redirect a country from php to pwa)
      const subscriptionId = getSubscriptionIdFromErrorMessage(error.message);
      if (subscriptionId) {
        yield put(actions.restoreUserSubscription({ subscriptionId }));
      }
    }

    yield put(actions.suscribeUserFailed(error));
  }
}

export function* updateUserSubscriptionSaga({ data }) {
  try {
    const { subscriptionId, subscription } = data;
    const result = yield call([notificationsApi, notificationsApi.updateUserSubscription], {
      subscriptionId,
      subscription,
    });
    if (result) {
      yield put(actions.updateUserSubscriptionSuccess({ user: result.data }));
    }
  } catch (error) {
    yield put(actions.suscribeUserFailed(error));
  }
}

export function* restoreUserSubscriptionSaga({ data }) {
  const { subscriptionId } = data;
  try {
    const result = yield call([notificationsApi, notificationsApi.getSearches], subscriptionId);
    if (result) {
      yield put(
        actions.restoreUserSubscriptionSuccess({ user: result.data, search: result.included })
      );
    }
  } catch (error) {
    yield put(actions.restoreUserSubscriptionFailed(error));
  }
}

export default function* notificationsSagas() {
  yield all([
    takeLatest(constants.SUSCRIPTION_USER, suscribeUserSaga),
    takeLatest(constants.SAVE_NOTIFICATION_SEARCH, saveSearchSaga),
    takeLatest(constants.FETCH_NOTIFICATION_SEARCHES, requestSearchesSaga),
    takeLatest(constants.UPDATE_NOTIFICATION_SEARCH, updateSearchSaga),
    takeLatest(constants.UPDATE_USER_SUBSCRIPTION, updateUserSubscriptionSaga),
    takeLatest(constants.RESTORE_USER_SUBSCRIPTION, restoreUserSubscriptionSaga),
  ]);
}



// WEBPACK FOOTER //
// ./app/Notifications/sagas.js